/**
 * HITL updated field highlight frame.
 * Callout around a NestedArgField / HITL argument leaf that changed after technician UPDATE.
 * Warm brown left border + previous-value caption ("Was: ...").
 */

import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { formatHitlPreviousArgValue } from "../hitl/hitlArgDiff";

const HITL_UPDATED_BORDER = "#C17F4A";
const HITL_UPDATED_BG = "#FBF3EA";
const HITL_UPDATED_WAS = "#8A6B52";
const HITL_UPDATED_CHIP_BORDER = "#E8C9A8";

interface HitlUpdatedFieldFrameProps {
  isUpdated?: boolean;
  previousValue?: unknown;
  fontSize?: number;
  children: React.ReactNode;
}

export const HitlUpdatedFieldFrame: React.FC<HitlUpdatedFieldFrameProps> = ({
  isUpdated = false,
  previousValue,
  fontSize = 14,
  children,
}) => {
  if (!isUpdated) {
    return <>{children}</>;
  }

  return (
    <Box
      // HITL updated field highlight: rectangular callout, reserved warm color
      sx={{
        pl: 1.5,
        ml: -1.5,
        pr: 1,
        py: 1,
        borderLeft: `3px solid ${HITL_UPDATED_BORDER}`,
        backgroundColor: HITL_UPDATED_BG,
      }}
    >
      {children}
      <Typography
        // HITL previous value caption
        fontSize={Math.max(fontSize - 2, 11)}
        fontWeight={500}
        color={HITL_UPDATED_WAS}
        fontFamily="var(--theo-font-sans)"
        sx={{ mt: 0.5, wordBreak: "break-word" }}
      >
        {`Was: ${formatHitlPreviousArgValue(previousValue)}`}
      </Typography>
    </Box>
  );
};

interface HitlUpdatedFieldsChipProps {
  compact?: boolean;
}

/** Accordion summary chip when a HITL tool has updated argument fields. */
export const HitlUpdatedFieldsChip: React.FC<HitlUpdatedFieldsChipProps> = ({
  compact = false,
}) => (
  <Chip
    size="small"
    label="Updated"
    sx={{
      flexShrink: 0,
      ml: 1,
      height: compact ? 22 : 24,
      fontFamily: "var(--theo-font-sans)",
      fontSize: compact ? 11 : 12,
      fontWeight: 700,
      borderRadius: "2px",
      backgroundColor: HITL_UPDATED_BG,
      color: HITL_UPDATED_BORDER,
      border: `1px solid ${HITL_UPDATED_CHIP_BORDER}`,
    }}
  />
);
